// ProfileCategories.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import FeedComponent from '../container/FeedComponent.js';

import { parseCategoryString } from '../component/FeedComponent.jsx';

import { HomeCategoryUi } from '../resource/feedConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import { ProfileListType } from '../resource/profileConstants.js';

import { color } from '../style/variables.js';
import media from '../style/media.js';

import UserEmptyPostIconSrouce from '../../img/img_empty_nopost.svg';

export const ProfileCategories = ({
  userId = null,
  profileFeedUnixTimestamp = null,
  isFetchedAndEmpty = false,
  categoryTemplates = [],
  categories = [],
}) => {
  const { t } = useTranslation();
  const i18nKeys = useMemo(
    () => categoryTemplates.map(key => getTranslationKey({ key })),
    [categoryTemplates]
  );
  const result = useMemo(() => {
    if (isFetchedAndEmpty) {
      return (
        <EmptyWrapper>
          <EmptyIcon src={UserEmptyPostIconSrouce} alt="no posts" />
          {t('empty_post_list', {
            ns: TranslationNamespace.PROFILE,
          })}
        </EmptyWrapper>
      );
    }
    return categories.map((category, index) => {
      const { ui } = parseCategoryString({ category });
      let titleLink;
      let locationState = undefined;
      switch (ui) {
        case HomeCategoryUi.FLIX: {
          titleLink = `/user/${userId}/${ProfileListType.FLIX}`;
          break;
        }
        case HomeCategoryUi.SHORTS:
        case HomeCategoryUi.SHORTS_METADATA_CARD: {
          titleLink = `/user/${userId}/${ProfileListType.SHORT}`;
          break;
        }
        case HomeCategoryUi.STORY:
        case HomeCategoryUi.STORY_METADATA_CARD:
        case HomeCategoryUi.STORY_GRID: {
          titleLink = `/user/${userId}/${ProfileListType.STORY}`;
          locationState = {
            listPath: ['outbox', ProfileListType.STORY, userId],
          };
          break;
        }
        case HomeCategoryUi.POST_CARD_GRID: {
          titleLink = `/user/${userId}/${ProfileListType.IMAGE}`;
          break;
        }
      }
      return (
        <ProfileCategory key={category} className={`categoryId_${category}`}>
          <FeedComponent
            category={category}
            categoryIndex={index}
            shouldShowMoreLink={false}
            titleI18nId={i18nKeys[index]}
            titleLink={titleLink}
            feedUnixTimestamp={profileFeedUnixTimestamp}
            locationState={locationState}
            isProfileCategory
            isTitleClickable
          />
        </ProfileCategory>
      );
    });
  }, [
    categories,
    i18nKeys,
    isFetchedAndEmpty,
    profileFeedUnixTimestamp,
    t,
    userId,
  ]);
  return result;
};

ProfileCategories.propTypes = {
  userId: PropTypes.string,
  profileFeedUnixTimestamp: PropTypes.number,
  isFetchedAndEmpty: PropTypes.bool,
  categoryTemplates: PropTypes.array,
  categories: PropTypes.array,
};

const EmptyWrapper = styled.div`
  padding-top: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-size: 14px;
  color: ${color.neutral[30]};
`;

const EmptyIcon = styled.img`
  margin-bottom: 12px;
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const ProfileCategory = styled.div`
  margin-bottom: 20px;
  :first-child {
    margin-top: 20px;
  }
  ${media.mobile`
    margin-bottom: 12px;
    :first-child {
      margin-top: 12px;
    }
  `};
`;

export default ProfileCategories;
